/**
 * Centralised mapping from raw errors (axios / fetch / generic) to
 * user-friendly messages shown in the UI.
 *
 * Keep the raw error around for `console.error` — only what this function
 * returns should ever be surfaced to users.
 *
 * Usage:
 *   try { ... } catch (err) {
 *     console.error("Error loading job data:", err);
 *     dispatch({ type: "SET_ERROR", payload: getUserFriendlyError(err, "loadJob") });
 *   }
 */

export type ErrorContext =
  | "loadJob"
  | "downloadReport"
  | "deleteJob"
  | "wizardSetup"
  | "wizardAgent"
  | "generic";

/** One user-friendly default per operation. */
const CONTEXT_MESSAGES: Record<ErrorContext, string> = {
  loadJob: "Unable to load this Recon. Please try again.",
  downloadReport: "Unable to download the report. Please try again.",
  deleteJob: "Unable to delete this Recon. Please try again.",
  wizardSetup:
    "We couldn't start the ledger setup. Please check your files and try again.",
  wizardAgent:
    "The setup assistant couldn't finish analyzing your files. Please try again.",
  generic: "Something didn't work. Please try again.",
};

/** Default copy for the Ledger Setup Assistant error screen. */
export const WIZARD_ERROR_TITLE = "We couldn't complete the setup";

/** Network-level (no response) fallback. Also used in permission-load error routing. */
export const NETWORK_ERROR_MESSAGE =
  "Can't reach the server. Check your Network connection and Network settings and try again.";

/**
 * Detect network/connectivity failures from error message strings.
 * Used when routing to /access-denied after CDS or other API load failures.
 */
export function isNetworkErrorMessage(error: string | null | undefined): boolean {
  if (!error) return false;
  const lower = error.toLowerCase();
  return (
    lower.includes("network") ||
    lower.includes("failed to fetch") ||
    lower.includes("unable to load") ||
    lower.includes("timeout") ||
    lower.includes("connection")
  );
}

/** Build query params for /access-denied when permissions failed to load (not empty permissions). */
export function buildAccessDeniedLoadErrorQuery(
  error: string,
  options?: { redirectUrl?: string; featureName?: string }
): Record<string, string> {
  return {
    isPermissionError: "true",
    errorMessage: error,
    isNoPermissionsError: "false",
    isApiError: "true",
    isNetworkError: isNetworkErrorMessage(error) ? "true" : "false",
    ...(options?.redirectUrl ? { redirectUrl: options.redirectUrl } : {}),
    ...(options?.featureName ? { featureName: options.featureName } : {}),
  };
}

type AxiosLikeError = {
  isAxiosError?: boolean;
  response?: { status?: number };
  request?: unknown;
  code?: string;
  message?: string;
};

const isAxiosLikeError = (err: unknown): err is AxiosLikeError => {
  return (
    typeof err === "object" &&
    err !== null &&
    ("isAxiosError" in err || "response" in err || "request" in err)
  );
};

const isNetworkError = (err: AxiosLikeError): boolean => {
  // axios: no response + has request => network error / CORS / DNS / offline
  if (err.response) return false;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  return !!err.request;
};

/**
 * Translate any thrown value into a user-friendly string.
 *
 * Precedence:
 *   1. Network error (no response) → NETWORK_ERROR_MESSAGE
 *   2. Otherwise                   → the context's default message
 *
 * The raw error message is intentionally NOT returned to the UI — backend
 * strings are rarely user-ready. Log the raw error separately with
 * `console.error` for debugging.
 */
export const getUserFriendlyError = (
  err: unknown,
  context: ErrorContext = "generic"
): string => {
  if (isAxiosLikeError(err) && isNetworkError(err)) {
    return NETWORK_ERROR_MESSAGE;
  }
  return CONTEXT_MESSAGES[context] ?? CONTEXT_MESSAGES.generic;
};

/**
 * Map raw backend / stream error strings to user-friendly copy.
 * Never surfaces job IDs, HTTP details, or internal service names.
 */
export function sanitizeRawErrorMessage(
  raw: string | null | undefined,
  fallback: string = CONTEXT_MESSAGES.generic
): string {
  if (!raw || raw.trim() === "") {
    return fallback;
  }

  const lower = raw.toLowerCase();

  if (
    lower.includes("local agent") ||
    lower.includes("connection attempts failed") ||
    lower.includes("connection refused") ||
    lower.includes("econnrefused")
  ) {
    return CONTEXT_MESSAGES.wizardAgent;
  }

  if (isNetworkErrorMessage(raw)) {
    return NETWORK_ERROR_MESSAGE;
  }

  if (
    lower.includes("http request") ||
    lower.includes("500") ||
    lower.includes("502") ||
    lower.includes("503") ||
    lower.includes("504") ||
    lower.includes("timeout") ||
    lower.includes("timed out")
  ) {
    return fallback;
  }

  return fallback;
}
